import type { ColumnDef } from '@tanstack/react-table';

type ColumnOptions<T> = {
  key: keyof T | string;
  id?: string;
  header: string;
  size?: number;
  minSize?: number;
  maxSize?: number;
  meta?: TableCreatorMeta;
  cell?: (info: any) => React.ReactElement;
  accessorFn?: (row: T) => any;
};

export class TableCreator<T> {
  private columns: ColumnDef<T>[];

  constructor() {
    this.columns = [];
  }

  addColumn(options: ColumnOptions<T>): TableCreator<T> {
    const column: ColumnDef<T> = {
      accessorKey: options.key as string,
      accessorFn: options.accessorFn,
      ...options,
      ...(options.meta && {
        meta: options.meta.getMeta(),
      }),
    };
    this.columns.push(column);
    return this;
  }

  getColumns(): ColumnDef<T>[] {
    return this.columns;
  }
}

export class TableCreatorMeta {
  private meta = {
    canSort: true,
  } as any;

  showNegative(): TableCreatorMeta {
    this.meta = {
      ...this.meta,
      showNegative: true,
    };

    return this;
  }

  showPositiveNegative(): TableCreatorMeta {
    this.meta = {
      ...this.meta,
      showPositiveNegative: true,
    };

    return this;
  }

  /**
   * mask: define the mask type for the column
   * thousandMask: if true, the column will be displayed in a compact format ex: 2.5k
   * @param mask
   * @param thousandMask
   * @returns
   */
  mask(mask: TableMaskType, thousandMask = false): TableCreatorMeta {
    this.meta = {
      ...this.meta,
      mask,
      thousandMask,
    };

    return this;
  }

  tooltip(tooltip: string): TableCreatorMeta {
    this.meta = {
      ...this.meta,
      tooltip,
    };

    return this;
  }

  subLine(
    key: string,
    mask?: TableMaskType | null,
    render?: React.ElementType
  ): TableCreatorMeta {
    this.meta = {
      ...this.meta,
      subLine: key,
      subLineMask: mask,
      subLineRender: render,
    };

    return this;
  }

  isHeader(): TableCreatorMeta {
    this.meta = {
      ...this.meta,
      isHead: true,
    };

    return this;
  }

  precision(precision: number | null, minPrecision?: number): TableCreatorMeta {
    this.meta = {
      ...this.meta,
      precision,
      minPrecision,
    };

    return this;
  }

  align(align: 'left' | 'right' | 'center'): TableCreatorMeta {
    this.meta = {
      ...this.meta,
      align,
    };

    return this;
  }

  showSign(): TableCreatorMeta {
    this.meta = {
      ...this.meta,
      showSign: true,
    };

    return this;
  }

  pinned(pinned: 'left' | 'right'): TableCreatorMeta {
    this.meta = {
      ...this.meta,
      pinned,
    };

    return this;
  }

  currency(currencyMask: 'BRL' | 'USD' | 'EUR' = 'BRL'): TableCreatorMeta {
    this.meta = {
      ...this.meta,
      currency: currencyMask,
    };

    return this;
  }

  isSorted(): TableCreatorMeta {
    this.meta = {
      ...this.meta,
      isSorted: true,
    };

    return this;
  }

  trophy(bestValue: TableTrophy = TableTrophy.HIGHEST): TableCreatorMeta {
    this.meta = {
      ...this.meta,
      trophy: bestValue,
    };

    return this;
  }

  isLink(target?: string | null): TableCreatorMeta {
    this.meta = {
      ...this.meta,
      isLink: true,
      target: target === undefined ? '_blank' : target,
    };

    return this;
  }

  removeSort(): TableCreatorMeta {
    this.meta = {
      ...this.meta,
      canSort: false,
    };

    return this;
  }

  trim(length: number = 30): TableCreatorMeta {
    this.meta = {
      ...this.meta,
      trim: length,
    };

    return this;
  }

  indicatorBar(colorKey: string = 'color'): TableCreatorMeta {
    this.meta = {
      ...this.meta,
      indicatorBar: true,
      indicatorColorKey: colorKey,
    };

    return this;
  }

  getMeta(): any {
    return this.meta;
  }
}

export enum TableTrophy {
  HIGHEST = 'highest',
  LOWEST = 'lowest',
}

export enum TableMaskType {
  USER = 'user',
  INTEGER = 'integer',
  DECIMAL = 'decimal',
  PERCENT = 'percent',
  PERCENT_OTHER = 'percent_other',
  CNPJ = 'cnpj',
  DATE = 'date',
  DATE_TIME = 'date_time',
  BOOLEAN = 'boolean',
  MONEY = 'money',
}
